import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export const authService = {
  login: (credentials) => api.post('/auth/login', credentials),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
  forgotPassword: (email) => api.post('/auth/forgot-password', { email }),
  getCurrentUser: () => {
    const user = localStorage.getItem('user');
    return user ? JSON.parse(user) : null;
  },
};

export const patientService = {
  getAllPatients: () => api.get('/patients'),
  getPatient: (id) => api.get(`/patients/${id}`),
  createPatient: (data) => api.post('/patients', data),
  updatePatient: (id, data) => api.put(`/patients/${id}`, data),
  deletePatient: (id) => api.delete(`/patients/${id}`),
  getPatientHistory: (id) => api.get(`/patients/${id}/history`),
  uploadLabReport: (id, formData) => 
    api.post(`/patients/${id}/lab-reports`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
};

export const appointmentService = {
  getAllAppointments: () => api.get('/appointments'),
  getTodayAppointments: () => api.get('/appointments/today'),
  getAppointment: (id) => api.get(`/appointments/${id}`),
  createAppointment: (data) => api.post('/appointments', data),
  updateAppointment: (id, data) => api.put(`/appointments/${id}`, data),
  deleteAppointment: (id) => api.delete(`/appointments/${id}`),
  getAppointmentsByDoctor: (doctorId) => api.get(`/appointments/doctor/${doctorId}`),
  getAppointmentsByPatient: (patientId) => api.get(`/appointments/patient/${patientId}`),
};

export const prescriptionService = {
  getPrescriptions: (patientId) => api.get(`/prescriptions/patient/${patientId}`),
  createPrescription: (data) => api.post('/prescriptions', data),
  getPrescriptionById: (id) => api.get(`/prescriptions/${id}`),
  getLastPrescription: (patientId) => api.get(`/prescriptions/patient/${patientId}/last`),
};

export const medicineService = {
  getAllMedicines: () => api.get('/medicines'),
  createMedicine: (data) => api.post('/medicines', data),
  updateMedicine: (id, data) => api.put(`/medicines/${id}`, data),
  deleteMedicine: (id) => api.delete(`/medicines/${id}`),
  getMedicinesByHotkey: (hotkey) => api.get(`/medicines/hotkey/${hotkey}`),
};

export const adviceService = {
  getAllAdvices: () => api.get('/advices'),
  createAdvice: (data) => api.post('/advices', data),
  updateAdvice: (id, data) => api.put(`/advices/${id}`, data),
  deleteAdvice: (id) => api.delete(`/advices/${id}`),
  getAdvicesByHotkey: (hotkey) => api.get(`/advices/hotkey/${hotkey}`),
};

export const userService = {
  getAllUsers: () => api.get('/users'),
  getUser: (id) => api.get(`/users/${id}`),
  createUser: (data) => api.post('/users', data),
  updateUser: (id, data) => api.put(`/users/${id}`, data),
  deleteUser: (id) => api.delete(`/users/${id}`),
  getDoctors: () => api.get('/users/role/doctor'),
};

export const printSettingsService = {
  getSettings: () => api.get('/print-settings'),
  updateSettings: (data) => api.put('/print-settings', data),
};

export const investigationService = {
  getAllInvestigations: () => api.get('/investigations'),
  createInvestigation: (data) => api.post('/investigations', data),
  updateInvestigation: (id, data) => api.put(`/investigations/${id}`, data),
  deleteInvestigation: (id) => api.delete(`/investigations/${id}`),
};

export const referDoctorService = {
  getAllReferDoctors: () => api.get('/referdoctors'),
  createReferDoctor: (data) => api.post('/referdoctors', data),
  updateReferDoctor: (id, data) => api.put(`/referdoctors/${id}`, data),
  deleteReferDoctor: (id) => api.delete(`/referdoctors/${id}`),
};

export const revenueAnalyticsService = {
  getSummary:      (from, to) => api.get('/revenueanalytics/summary',      { params: { from, to } }),
  getTrend:        (from, to) => api.get('/revenueanalytics/trend',        { params: { from, to } }),
  getPaymentModes: (from, to) => api.get('/revenueanalytics/payment-modes', { params: { from, to } }),
  getByService:    (from, to) => api.get('/revenueanalytics/by-service',   { params: { from, to } }),
  getByType:       (from, to) => api.get('/revenueanalytics/by-type',      { params: { from, to } }),
  getByDoctor:     (from, to) => api.get('/revenueanalytics/by-doctor',    { params: { from, to } }),
  getRecentBills:  (from, to) => api.get('/revenueanalytics/recent-bills', { params: { from, to } }),
};

export const serviceRateService = {
  getAll: () => api.get('/servicerates'),
  create: (data) => api.post('/servicerates', data),
  update: (id, data) => api.put(`/servicerates/${id}`, data),
  delete: (id) => api.delete(`/servicerates/${id}`),
};

export const billingService = {
  createBill: (data) => api.post('/bills', data),
  getAllBills: () => api.get('/bills'),
  getBillsByPatient: (patientId) => api.get(`/bills/patient/${patientId}`),
  getBill: (id) => api.get(`/bills/${id}`),
};

export const doctorPerformanceService = {
  getSummary:           (doctorId, from, to) => api.get('/doctorperformance/summary',             { params: { doctorId, from, to } }),
  getTrend:             (doctorId, from, to) => api.get('/doctorperformance/trend',               { params: { doctorId, from, to } }),
  getPatientTypes:      (doctorId, from, to) => api.get('/doctorperformance/patient-types',       { params: { doctorId, from, to } }),
  getStatusBreakdown:   (doctorId, from, to) => api.get('/doctorperformance/status-breakdown',    { params: { doctorId, from, to } }),
  getTopMedicines:      (doctorId, from, to) => api.get('/doctorperformance/top-medicines',       { params: { doctorId, from, to } }),
  getComparison:        (from, to)           => api.get('/doctorperformance/comparison',          { params: { from, to } }),
  getRecentAppointments:(doctorId, from, to) => api.get('/doctorperformance/recent-appointments', { params: { doctorId, from, to } }),
};

export const dischargeSummaryService = {
  save: (data) => api.post('/dischargesummary', data),
  getByAppointment: (appointmentId) => api.get(`/dischargesummary/appointment/${appointmentId}`),
};

export const mediaService = {
  getAllMedia: () => api.get('/media'),
  uploadMedia: (formData) =>
    api.post('/media', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }),
  updateMedia: (id, data) => api.put(`/media/${id}`, data),
  deleteMedia: (id) => api.delete(`/media/${id}`),
};

export const issuedCertificateService = {
  save:       (data)    => api.post('/issued-certificates', data),
  getAll:     (params)  => api.get('/issued-certificates', { params }),
  getByCertNo:(certNo)  => api.get(`/issued-certificates/${certNo}`),
};

export const attendanceService = {
  getAll:   (params) => api.get('/attendance', { params }),
  upsert:   (data)   => api.post('/attendance', data),
  bulkMark: (data)   => api.post('/attendance/bulk', data),
  delete:   (id)     => api.delete(`/attendance/${id}`),
};

export const leaveService = {
  getAll:  (params)   => api.get('/leaverequests', { params }),
  getMy:   (params)   => api.get('/leaverequests/my', { params }),
  create:  (data)     => api.post('/leaverequests', data),
  update:  (id, data) => api.put(`/leaverequests/${id}`, data),
  delete:  (id)       => api.delete(`/leaverequests/${id}`),
};

export const salaryStructureService = {
  getAll:    ()       => api.get('/salarystructure'),
  getByUser: (userId) => api.get(`/salarystructure/${userId}`),
  upsert:    (data)   => api.post('/salarystructure', data),
};

export const salarySlipService = {
  getAll:   (params) => api.get('/salaryslips', { params }),
  generate: (data)   => api.post('/salaryslips/generate', data),
  markPaid: (id)     => api.put(`/salaryslips/${id}/mark-paid`),
  delete:   (id)     => api.delete(`/salaryslips/${id}`),
};

export const doctorFavouriteService = {
  getMedicines: ()             => api.get('/doctorfavourites/medicines'),
  addMedicine:  (id)           => api.post(`/doctorfavourites/medicines/${id}`),
  removeMedicine: (id)         => api.delete(`/doctorfavourites/medicines/${id}`),
  updateMedicineHotkey: (id, hotkey) => api.put(`/doctorfavourites/medicines/${id}/hotkey`, { hotkey }),
  getAdvices:   ()             => api.get('/doctorfavourites/advices'),
  addAdvice:    (id)           => api.post(`/doctorfavourites/advices/${id}`),
  removeAdvice: (id)           => api.delete(`/doctorfavourites/advices/${id}`),
  updateAdviceHotkey: (id, hotkey) => api.put(`/doctorfavourites/advices/${id}/hotkey`, { hotkey }),
};

export const expenseService = {
  getAll:        (params) => api.get('/expenses', { params }),
  getSummary:    (params) => api.get('/expenses/summary', { params }),
  getMonthlyTrend: (year) => api.get('/expenses/monthly-trend', { params: { year } }),
  create:        (data)   => api.post('/expenses', data),
  update:        (id, data) => api.put(`/expenses/${id}`, data),
  delete:        (id)     => api.delete(`/expenses/${id}`),
  uploadAttachment: (id, file) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post(`/expenses/${id}/attachment`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  deleteAttachment: (id) => api.delete(`/expenses/${id}/attachment`),
};

export default api;
